import React from 'react';
import { Icon, type IconName } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';
import { openDrawer } from 'Services/Drawer';
import type PublicChatbotConversation from 'App/resources/Model/PublicChatbotConversation';
import { handOverDeleted, handOverKind } from './conversationDisplay';
import './WebsiteChat.scss';

type Props = {
	conversation: Pick<
		PublicChatbotConversation,
		'outcome' | 'escalatedEntity' | 'escalatedEntityId' | 'escalatedActivityTypeId' | 'escalatedTitle'
	>;
};

// One icon per kind, matched by ui-3's badge (ENTITY_ICONS todo / phonecall / ticket).
const ICON: Record<NonNullable<ReturnType<typeof handOverKind>>, IconName> = {
	todo: 'check',
	phonecall: 'phone',
	ticket: 'ticket'
};

/**
 * How a website chat ended, as a badge (WEB-12989). The outcome is derived on
 * the server (the repo's `outcome` extraField, which documents the rule). A
 * hand-over badge is a link to the record it created: a real `/open` URL (new
 * tab, copy) that opens the ticket or to-do in place on a plain click. Each
 * kind has its own icon and colour (WEB-13681), so a phone call is told from
 * a to-do at a glance.
 */
const WebsiteChatOutcome = ({ conversation }: Props) => {
	const classes = new BemClass('WebsiteChatOutcome');
	const { t } = useTranslation();
	const { escalatedEntity: entity, escalatedEntityId: id } = conversation;

	// The record was deleted: named, without a link to nowhere. A deleted phone call reads as a to-do - its type went with it.
	if (entity && handOverDeleted(conversation)) {
		const outcome =
			entity === 'ticket' ? t('publicChatbot.chat.outcome.ticket', { id }) : t('publicChatbot.chat.outcome.todo');
		return (
			<span className={classes.mod('deleted').b()} data-testid="chat-outcome" data-kind="deleted">
				{t('publicChatbot.chat.outcome.deleted', { outcome })}
			</span>
		);
	}
	if (entity && id) {
		const kind = handOverKind(conversation) ?? 'todo';
		const open = (e: React.MouseEvent) => {
			e.stopPropagation();
			if (e.metaKey || e.ctrlKey || e.shiftKey) return;
			e.preventDefault();
			if (kind === 'ticket') {
				openModal('EditTicket', { ticketId: id });
			} else if (kind === 'phonecall') {
				openDrawer('EditPhonecall', { activity: { id } });
			} else {
				openDrawer('EditTodo', { todo: { id } });
			}
		};
		return (
			<a
				href={`#/open/${kind === 'phonecall' ? 'phoneCall' : kind}/${id}`}
				onClick={open}
				className={classes.mod(kind).b()}
				data-testid="chat-outcome"
				data-kind={kind}
			>
				<Icon name={ICON[kind]} className={classes.elem('icon').b()} />
				{t(`publicChatbot.chat.outcome.${kind}`, { id })}
			</a>
		);
	}
	const outcome = conversation.outcome === 'unanswered' ? 'unanswered' : 'answered';
	return (
		<span className={classes.mod(outcome).b()} data-testid="chat-outcome">
			{t(`publicChatbot.chat.outcome.${outcome}`)}
		</span>
	);
};

export default WebsiteChatOutcome;
